import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Music, Calendar, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { releases, dubplates } from '../mock/data';

const MemberPortal = () => {
  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { user, getToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const fetchPortalData = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/portal`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch portal data');
      }

      const data = await response.json();
      setPortalData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching portal data:', error);
      alert('Failed to load portal. Please try again.');
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/login?redirect=/member');
      return;
    }
    fetchPortalData();
  }, [user, navigate, authLoading, fetchPortalData]);

  const handleTrackSelection = (trackName) => {
    if (selectedTracks.includes(trackName)) {
      setSelectedTracks(selectedTracks.filter(t => t !== trackName));
    } else {
      const maxTracks = getMaxTracks();
      if (selectedTracks.length < maxTracks) {
        setSelectedTracks([...selectedTracks, trackName]);
      } else {
        alert(`You can only select ${maxTracks} tracks this month.`);
      }
    }
  };

  const getMaxTracks = () => {
    if (!portalData?.subscription) return 0;
    // Single tier: £4.99 allows selection of tracks from back catalogue
    // Initial signup: 3 tracks, Monthly: 2 tracks
    // The backend tracks_this_month field determines the current limit
    return portalData.subscription.tracks_this_month || 2;
  };

  const handleSubmitSelection = async () => {
    if (selectedTracks.length === 0) {
      alert('Please select at least one track.');
      return;
    }

    setSubmitting(true);

    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/select-tracks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          track_names: selectedTracks
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit selections');
      }

      setSubmitted(true);
      fetchPortalData(); // Refresh data
    } catch (error) {
      console.error('Error submitting tracks:', error);
      alert(error.message || 'Failed to submit track selections. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (!portalData?.subscription || portalData.subscription.status !== 'active') {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">No Active Subscription</h1>
          <p className="text-gray-400 mb-8">You need an active VIP subscription to access the member portal.</p>
          <button
            onClick={() => navigate('/vip')}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  const tier = portalData.subscription.tier || '499';
  const tierName = 'VIP MEMBER';
  const tierPrice = '£4.99';
  const maxTracks = getMaxTracks();

  // Single tier: all VIP members get access to full back catalogue including dubplates
  const backCatalogue = [
    ...releases.map(r => ({ ...r, type: 'release' })),
    ...dubplates.map(d => ({ ...d, type: 'dubplate' }))
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-zinc-950 to-black py-12 border-b border-red-500/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-center mb-2">
            MEMBER PORTAL
          </h1>
          <p className="text-center text-gray-400 mb-6">Welcome back, {portalData.user.name}</p>
          
          {/* Subscription Status Card */}
          <div className="max-w-md mx-auto bg-gradient-to-br from-red-950/50 to-zinc-900/50 border-2 border-red-500/50 rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold text-red-400">{tierName}</h3>
                <p className="text-gray-400">{tierPrice}/month</p>
              </div>
              <div className="px-3 py-1 bg-green-500/20 border border-green-500/50 rounded-full text-green-400 text-sm font-semibold">
                ACTIVE
              </div>
            </div>
            <div className="text-sm text-gray-400">
              <p>Monthly Track Allowance: <strong className="text-white">{maxTracks} tracks</strong></p>
            </div>
          </div>
        </div>
      </section>

      {/* Track Selection Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <div className="max-w-2xl mx-auto bg-green-950/30 border-2 border-green-500/50 rounded-xl p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Tracks Requested!</h2>
              <p className="text-gray-300 mb-6">
                Cheers! Barry will email your download links within 24 hours to <strong>{portalData.user.email}</strong>
              </p>
              <p className="text-sm text-gray-400">
                Your selected tracks: {selectedTracks.join(', ')}
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">SELECT YOUR TRACKS</h2>
                <p className="text-gray-400">
                  Choose {maxTracks} track{maxTracks > 1 ? 's' : ''} from the back catalogue below
                </p>
                <div className="mt-4 inline-block px-4 py-2 bg-red-600/20 border border-red-500/50 rounded-lg">
                  <p className="text-red-400 font-semibold">
                    {selectedTracks.length} / {maxTracks} selected
                  </p>
                </div>
              </div>

              {/* Back Catalogue Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {backCatalogue.map((track) => {
                  const isSelected = selectedTracks.includes(track.title);
                  return (
                    <div
                      key={track.id}
                      onClick={() => handleTrackSelection(track.title)}
                      className={`cursor-pointer bg-zinc-900 border-2 rounded-xl p-4 transition-all duration-300 ${
                        isSelected
                          ? 'border-red-500 shadow-[0_0_30px_rgba(220,38,38,0.4)]'
                          : 'border-zinc-700 hover:border-red-500/50'
                      }`}
                    >
                      <img
                        src={track.artwork}
                        alt={track.title}
                        className="w-full aspect-square object-cover rounded-lg mb-3"
                      />
                      <h3 className="font-bold text-white mb-1">{track.title}</h3>
                      <p className="text-sm text-gray-400 mb-2">{track.artist}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{track.bpm} BPM</span>
                        {isSelected && (
                          <Check className="w-5 h-5 text-red-400" />
                        )}
                      </div>
                      {track.type === 'dubplate' && (
                        <div className="mt-2 px-2 py-1 bg-red-500/20 rounded text-xs text-red-400 font-semibold">
                          VIP DUB
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  onClick={handleSubmitSelection}
                  disabled={selectedTracks.length === 0 || submitting}
                  className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-lg rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : `Request ${selectedTracks.length} Track${selectedTracks.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Previous Selections */}
      {portalData.track_selections && portalData.track_selections.length > 0 && (
        <section className="py-12 bg-zinc-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold mb-6">Your Previous Requests</h2>
            <div className="space-y-4">
              {portalData.track_selections.slice(0, 5).map((selection) => (
                <div key={selection.id} className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-white">{selection.track_name}</p>
                    <p className="text-sm text-gray-400">
                      Requested: {new Date(selection.selected_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    selection.delivered
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {selection.delivered ? 'Delivered' : 'Pending'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MemberPortal;
